import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { useState } from "react";
import { useConfirm } from "../UI/Feedback/useConfirm.ts";
import { Layout } from "../UI/Layout/Layout.tsx";
import { Card } from "../UI/Card.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { useAllProgressCheckPdfs } from "../ProgressCheckPdf/useAllProgressCheckPdfs.ts";
import { ProgressCheckPdfTable } from "../ProgressCheckPdf/ProgressCheckPdfTable.tsx";
import { useToCreateProgressCheckPdfScreen } from "../ProgressCheckPdf/useToCreateProgressCheckPdfScreen.ts";
import { useToEditProgressCheckPdfScreen } from "../ProgressCheckPdf/useToEditProgressCheckPdfScreen.ts";
import { useAllOneOnOneAssignmentPdfs } from "../OneOnOneAssignmentPdf/useAllOneOnOneAssignmentPdfs.ts";
import { OneOnOneAssignmentPdfGroupedView } from "../OneOnOneAssignmentPdf/OneOnOneAssignmentPdfGroupedView.tsx";
import { useToCreateOneOnOneAssignmentPdfScreen } from "../OneOnOneAssignmentPdf/useToCreateOneOnOneAssignmentPdfScreen.ts";
import { useToEditOneOnOneAssignmentPdfScreen } from "../OneOnOneAssignmentPdf/useToEditOneOnOneAssignmentPdfScreen.ts";
import { useDeleteOneOnOneAssignmentPdf } from "../OneOnOneAssignmentPdf/useDeleteOneOnOneAssignmentPdf.ts";
import {
  BusinessEnglishObjective,
  OneOnOneLessonType,
  SpeakingClassLevel,
} from "../gql/graphql.ts";
import { IconButton } from "../UI/IconButton.tsx";
import { OnboardingLivePdfSection } from "../OnboardingLive/Pdf/OnboardingLivePdfSection.tsx";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FolderIcon,
  FolderOpenIcon,
  PlusIcon,
} from "@heroicons/react/24/outline/index.js";

export function CoursePdfsScreen() {
  const [groupOpen, setGroupOpen] = useState(false);
  const [oneOnOneOpen, setOneOnOneOpen] = useState(false);

  const { pdfs, loading } = useAllProgressCheckPdfs();
  const { toCreate: toCreateGroupPdf } = useToCreateProgressCheckPdfScreen();
  const { toEdit: toEditGroupPdf } = useToEditProgressCheckPdfScreen();

  const { pdfs: oneOnOnePdfs, loading: loading1on1 } =
    useAllOneOnOneAssignmentPdfs();
  const { toCreate: toCreate1on1Pdf } =
    useToCreateOneOnOneAssignmentPdfScreen();
  const { toEdit: toEdit1on1Pdf } = useToEditOneOnOneAssignmentPdfScreen();
  const { deleteAssignmentPdf } = useDeleteOneOnOneAssignmentPdf();
  const askConfirmation = useConfirm();

  async function handleDelete1on1Pdf(row: {
    level: string;
    userGroupId: string;
    oneOnOneLessonType: string;
    assignmentNumber: number;
    businessEnglishObjective?: string | null;
  }) {
    const confirmed = await askConfirmation({
      title: `Delete assignment PDF #${row.assignmentNumber}?`,
      message: `${row.level} – ${row.oneOnOneLessonType}${row.businessEnglishObjective ? ` – ${row.businessEnglishObjective}` : ""}`,
      confirmLabel: "Delete",
      isDestructive: true,
    });
    if (!confirmed) return;
    await deleteAssignmentPdf({
      level: row.level as SpeakingClassLevel,
      userGroupId: row.userGroupId,
      oneOnOneLessonType: row.oneOnOneLessonType as OneOnOneLessonType,
      assignmentNumber: row.assignmentNumber,
      businessEnglishObjective: row.businessEnglishObjective as
        | BusinessEnglishObjective
        | undefined,
    });
  }

  const adultsPdfs = pdfs.filter(
    (p) => !p.userGroup.slug.toLowerCase().includes("teens"),
  );
  const teensPdfs = pdfs.filter((p) =>
    p.userGroup.slug.toLowerCase().includes("teens"),
  );

  return (
    <Layout>
      <Card>
        <ScreenTitleBlock title={"Course PDFs"} />
      </Card>

      <div className={"mt-4"}>
        <Card>
          <div className={"flex items-center justify-between gap-3"}>
            <button
              onClick={() => setGroupOpen((v) => !v)}
              className={
                "flex items-center gap-2 text-left hover:text-slate-600 transition-colors"
              }
            >
              {groupOpen ? (
                <FolderOpenIcon className={"w-5 h-5 text-slate-500"} />
              ) : (
                <FolderIcon className={"w-5 h-5 text-slate-500"} />
              )}
              <span className={"font-bold text-xl"}>
                Group Progress Check PDFs
              </span>
              {groupOpen ? (
                <ChevronDownIcon className={"w-4 h-4 text-slate-400"} />
              ) : (
                <ChevronRightIcon className={"w-4 h-4 text-slate-400"} />
              )}
            </button>
            <IconButton
              label={"New Group PDF"}
              icon={PlusIcon}
              onClick={toCreateGroupPdf}
            />
          </div>

          {groupOpen && (
            <div className={"mt-4"}>
              {loading ? (
                <LoadingRows />
              ) : (
                <>
                  <div>
                    <h3
                      className={"font-semibold text-base mb-2 text-slate-600"}
                    >
                      Adults
                    </h3>
                    <ProgressCheckPdfTable
                      items={adultsPdfs}
                      onEdit={(row) =>
                        toEditGroupPdf(row.level, row.userGroupId)
                      }
                    />
                  </div>
                  <div className={"mt-6"}>
                    <h3
                      className={"font-semibold text-base mb-2 text-slate-600"}
                    >
                      Teens
                    </h3>
                    <ProgressCheckPdfTable
                      items={teensPdfs}
                      onEdit={(row) =>
                        toEditGroupPdf(row.level, row.userGroupId)
                      }
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </Card>
      </div>

      <div className={"mt-4"}>
        <Card>
          <div className={"flex items-center justify-between gap-3"}>
            <button
              onClick={() => setOneOnOneOpen((v) => !v)}
              className={
                "flex items-center gap-2 text-left hover:text-slate-600 transition-colors"
              }
            >
              {oneOnOneOpen ? (
                <FolderOpenIcon className={"w-5 h-5 text-slate-500"} />
              ) : (
                <FolderIcon className={"w-5 h-5 text-slate-500"} />
              )}
              <span className={"font-bold text-xl"}>1on1 Assignment PDFs</span>
              {oneOnOneOpen ? (
                <ChevronDownIcon className={"w-4 h-4 text-slate-400"} />
              ) : (
                <ChevronRightIcon className={"w-4 h-4 text-slate-400"} />
              )}
            </button>
            <IconButton
              label={"New 1on1 Assignment PDF"}
              icon={PlusIcon}
              onClick={toCreate1on1Pdf}
            />
          </div>

          {oneOnOneOpen && (
            <div className={"mt-4"}>
              {loading1on1 ? (
                <LoadingRows />
              ) : (
                <OneOnOneAssignmentPdfGroupedView
                  items={oneOnOnePdfs}
                  onEdit={(row) =>
                    toEdit1on1Pdf(
                      row.level,
                      row.userGroupId,
                      row.oneOnOneLessonType,
                      row.assignmentNumber,
                      row.businessEnglishObjective ?? undefined,
                    )
                  }
                  onDelete={handleDelete1on1Pdf}
                />
              )}
            </div>
          )}
        </Card>
      </div>

      <div className={"mt-4"}>
        <OnboardingLivePdfSection />
      </div>
    </Layout>
  );
}
